'use client';

import { useReveal } from '@/components/marketing/engine/use-reveal';

/*──────────────────────────────────────────────────────────────────────
 * S7 — CONFIDENCE SECTION
 * Story beat: CONFIDENCE. The journey resolved — scattered signals in,
 * one decision out. Traceable, defensible, logged end to end.
 *──────────────────────────────────────────────────────────────────────*/

export function ConfidenceSection() {
  const { ref, shown } = useReveal(0.2);

  const fade = (_n: number, d = 0): React.CSSProperties => ({
    opacity: shown ? 1 : 0,
    transform: shown ? 'translateY(0)' : 'translateY(14px)',
    transition: `all .9s var(--ease) ${d}s`,
  });

  return (
    <section
      ref={ref}
      id="confidence"
      className="section section--airy"
      style={{
        /* Narrative color-temperature arc — closing beat: a faint green "confidence /
           sealed" undertone behind the resolved CTA, layered over the indigo field.
           Mirrors the crimson opening in Fragmentation. Drop the first gradient to revert. */
        background:
          'radial-gradient(ellipse 82% 66% at 50% 26%, color-mix(in srgb, var(--green) 6%, transparent) 0%, transparent 60%), var(--bg-veil)',
        overflow: 'hidden',
        position: 'relative',
        textAlign: 'center',
      }}
    >
      {/* settled convergence glow */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '14%',
          transform: 'translateX(-50%)',
          width: '620px',
          height: '620px',
          maxWidth: '120vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, var(--spill), transparent 62%)',
          filter: 'blur(30px)',
          opacity: shown ? 1 : 0,
          transition: 'opacity 1.4s ease',
          pointerEvents: 'none',
        }}
      ></div>
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: 0,
          width: '150vw',
          height: '1px',
          transform: 'translateX(-50%)',
          background:
            'linear-gradient(90deg, transparent, var(--border-hi) 40%, var(--indigo-light) 50%, var(--border-hi) 60%, transparent)',
          opacity: shown ? 1 : 0,
          transition: 'opacity 1s ease',
          animation: 'shimmer-line 6s ease-in-out infinite',
        }}
      ></div>

      <div
        className="section-inner"
        style={{ maxWidth: '640px', position: 'relative', zIndex: 2 }}
      >
        {/* resolved verified mark */}
        <div style={{ display: 'inline-flex', marginBottom: '32px', ...fade(1) }}>
          <div
            style={{
              position: 'relative',
              width: '76px',
              height: '76px',
              borderRadius: '50%',
              background: 'var(--green-soft)',
              border:
                '1px solid color-mix(in srgb, var(--green) 30%, transparent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 40px var(--green-spill)',
            }}
          >
            <span
              style={{
                position: 'absolute',
                inset: '-1px',
                borderRadius: '50%',
                border: '1px solid var(--green)',
                animation: 'node-pulse 3s ease-in-out infinite',
              }}
            ></span>
            <svg
              width="34"
              height="34"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--green)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
        </div>

        <span
          className="label"
          style={{
            color: 'var(--indigo-light)',
            display: 'block',
            marginBottom: '20px',
            ...fade(1, 0.05),
          }}
        >
          04 · CONFIDENCE
        </span>

        <h2
          className="serif"
          style={{
            fontSize: 'var(--h2)',
            lineHeight: 1.02,
            letterSpacing: '-0.01em',
            ...fade(2, 0.1),
          }}
        >
          Stop guessing.
          <br />
          <span style={{ fontStyle: 'italic', color: 'var(--indigo-light)' }}>
            Hire with proof.
          </span>
        </h2>

        <p
          style={{
            fontSize: 'var(--body)',
            color: 'var(--text-2)',
            lineHeight: 1.6,
            margin: '22px auto 0',
            maxWidth: '440px',
            ...fade(3, 0.2),
          }}
        >
          Scattered signals in. One decision out. Traceable, defensible, and
          logged end to end.
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '14px',
            justifyContent: 'center',
            marginTop: '38px',
            ...fade(4, 0.3),
          }}
        >
          {/* Closing CTA — same invite-only story as the hero. */}
          <a
            href="/request-access"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 34px',
              borderRadius: 'var(--r-full)',
              background: 'var(--indigo)',
              color: '#fff',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: 600,
              boxShadow:
                '0 0 40px rgba(99,102,241,0.4), inset 0 1px 0 rgba(255,255,255,0.15)',
              transition: 'transform .2s, box-shadow .3s',
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow =
                '0 0 56px rgba(99,102,241,0.6), inset 0 1px 0 rgba(255,255,255,0.15)';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow =
                '0 0 40px rgba(99,102,241,0.4), inset 0 1px 0 rgba(255,255,255,0.15)';
            }}
          >
            Request access
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

export default ConfidenceSection;
